// MCHD Room Scheduler - Building Tree Hook
// Created: 07-10-2025 14:01:32
// Purpose: Fetch and transform buildings/floors/rooms into hierarchical tree structure

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from '../../api/index';
import type { BuildingTreeData } from '../../components/organisms/BuildingTreeView';

/**
 * Fetch all buildings with nested floors and rooms
 * 
 * Transforms flat API responses into hierarchical tree structure
 * for use with BuildingTreeView component.
 * 
 * @returns Promise with hierarchical building data
 * 
 * Created: 07-10-2025 14:01:32
 */
const fetchBuildingTree = async (): Promise<BuildingTreeData[]> => {
  try {
    // Fetch all buildings (with high limit to get all)
    // api.get() returns JSON directly: { success: true, data: { buildings: [...], pagination: {...} } }
    const buildingsResponse = await api.get('/buildings?limit=100');
    const buildings = buildingsResponse.data?.buildings || [];

    // For each building, fetch its floors with rooms
    const buildingsWithNesting = await Promise.all(
      buildings.map(async (building: any) => {
        try {
          // Fetch floors for this building
          // api.get() returns JSON directly: { success: true, data: { floors: [...], pagination: {...} } }
          const floorsResponse = await api.get(`/floors?buildingId=${building.id}&limit=100`);
          const floors = floorsResponse.data?.floors || [];

          // For each floor, fetch its rooms
          const floorsWithRooms = await Promise.all(
            floors.map(async (floor: any) => {
              try {
                // api.get() returns JSON directly: { success: true, data: [...rooms...], meta: {...} }
                const roomsResponse = await api.get(`/rooms/floor/${floor.id}`);
                const rooms = roomsResponse.data || [];

                return {
                  id: floor.id,
                  name: floor.name,
                  number: floor.number,
                  buildingId: floor.buildingId,
                  rooms: rooms.map((room: any) => ({
                    id: room.id,
                    number: room.number,
                    name: room.name,
                    isActive: room.isActive,
                    capacity: room.capacity,
                    floorId: room.floorId,
                  })),
                };
              } catch (error) {
                console.error(`Error fetching rooms for floor ${floor.id}:`, error);
                return {
                  id: floor.id,
                  name: floor.name,
                  number: floor.number,
                  buildingId: floor.buildingId,
                  rooms: [],
                };
              }
            })
          );

          return {
            id: building.id,
            name: building.name,
            isActive: building.isActive,
            floors: floorsWithRooms,
          };
        } catch (error) {
          console.error(`Error fetching floors for building ${building.id}:`, error);
          return {
            id: building.id,
            name: building.name,
            isActive: building.isActive,
            floors: [],
          };
        }
      })
    );

    return buildingsWithNesting;
  } catch (error: any) {
    console.error('[Building Tree] Error:', error);
    throw new Error(error.message || 'Failed to fetch building tree');
  }
};

/**
 * Use Building Tree
 * 
 * React Query hook to fetch hierarchical building structure.
 * Returns Buildings → Floors → Rooms in nested format.
 * 
 * @returns UseQueryResult with building tree data
 * 
 * @example
 * ```tsx
 * const { data: buildingTree, isLoading, error } = useBuildingTree();
 * ```
 * 
 * Created: 07-10-2025 14:01:32
 * Complies: @react-frontend.mdc, @tech-stack-documentation.mdc
 */
export function useBuildingTree(): UseQueryResult<BuildingTreeData[], Error> {
  return useQuery({
    queryKey: ['buildings', 'list', { limit: 100 }],
    queryFn: fetchBuildingTree,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
  });
}

export default useBuildingTree;

